import type { BankTxn, BankTxnGroup } from './ViewBankTransactionsDialog'

/** A text field, or an em dash for an empty one. */
export function renderOrDash(value: string): string {
  return value === '' ? '—' : value
}

/** One transaction of the flattened feed, tagged with the group it came from. */
export interface FlatTxn {
  txn: BankTxn
  /** Heading text of the group — display only. */
  groupName: string
  /**
   * Group identity for boundary detection: `BankTxnGroup.id`, else the name.
   * Not the name alone — `facilities.name` is not unique, so two facilities of
   * one pack can share a heading text and must still get two headings.
   */
  groupKey: string
}

/**
 * A row of the drill-down table. Only `transaction` rows are transactions:
 * the opening balance is a baseline figure and headings are labels, so
 * neither occupies a page slot nor counts toward the transactions.
 */
export type TxnDialogRow =
  | { kind: 'opening'; amount: number }
  | { kind: 'heading'; key: string; name: string; continued: boolean }
  | { kind: 'transaction'; txn: BankTxn }

/**
 * The whole feed as one ordered list — what pagination slices. Order is the
 * caller's (facility, then date desc) and must not change: it keeps each
 * group's rows contiguous, which the headings rely on.
 *
 * Paging is flat rather than per group because a pack's groups are uneven
 * (one member with 1200 rows, another with 3).
 */
export function flattenGroups(groups: BankTxnGroup[]): FlatTxn[] {
  return groups.flatMap((group) =>
    group.transactions.map((txn) => ({
      txn,
      groupName: group.name,
      groupKey: group.id ?? group.name,
    }))
  )
}

/** Input of {@link buildPageRows}. */
export interface PageRowsInput {
  flat: FlatTxn[]
  /** Index in `flat` of the page's first transaction. */
  offset: number
  pageSize: number
  /** Pack drill: label each facility block (the modal title is the pack). */
  showGroupHeadings: boolean
  /** Starting LOC usage; shown as the opening row on page 1 when non-zero. */
  startingBalance: number
}

/**
 * Rows of one page: the opening-balance row (page 1 only — restated above a
 * later page it would be arithmetic nonsense), then the page's transactions
 * with a heading wherever a group starts. A page that opens part-way through
 * a group repeats its heading marked `continued`, so every block stays
 * attributable to its facility without paging back.
 */
export function buildPageRows({
  flat,
  offset,
  pageSize,
  showGroupHeadings,
  startingBalance,
}: PageRowsInput): TxnDialogRow[] {
  const rows: TxnDialogRow[] = []
  if (offset === 0 && startingBalance !== 0) rows.push({ kind: 'opening', amount: startingBalance })

  const page = flat.slice(offset, offset + pageSize)
  page.forEach(({ txn, groupName, groupKey }, index) => {
    if (showGroupHeadings) {
      // The page's first row compares with the previous page's last row.
      const previous = index === 0 ? flat[offset - 1] : page[index - 1]
      const startsNewGroup = previous?.groupKey !== groupKey
      const continued = index === 0 && !startsNewGroup
      if (startsNewGroup || continued) {
        rows.push({ kind: 'heading', key: `heading:${txn.id}`, name: groupName, continued })
      }
    }
    rows.push({ kind: 'transaction', txn })
  })
  return rows
}
